import { RedactedBalance } from "Components/Shared/RedactedBalance";
import { useCurrencySymbol } from "Hooks/useCurrencySymbol";
import type { CurrencyCode } from "Models/CurrencyCodes";
import { Box, Skeleton, styled, Typography } from "@mui/material";

export type Props = {
  balance: number;
  // Any ISO 4217 code — API-enum and plain-string callers pass through uncast.
  currency?: CurrencyCode | string;
  prefix?: string;
  isLoading?: boolean;
  isBigCurrency?: boolean;
  colorVariant?: "light" | "dark";
  isRedacted?: boolean;
};

const Currency = styled(Typography)`
  display: flex;
  flex-direction: column;
  justify-content: end;
`;

export const Balance = ({
  balance,
  currency,
  prefix,
  isLoading,
  isBigCurrency,
  isRedacted = false,
  colorVariant = "dark",
}: Props) => {
  // No currency -> no symbol. Money must never fall back to a guessed currency.
  const currencySymbol = useCurrencySymbol(currency);

  const textColor =
    colorVariant === "light" ? "primary.contrastText" : "primary.main";
  const redactedBalanceColor =
    colorVariant === "light" ? "primary.contrastText" : undefined;

  if (isRedacted) {
    return (
      <RedactedBalance
        size={8}
        color={redactedBalanceColor}
        iconColor={redactedBalanceColor}
      />
    );
  }

  const formattedValue =
    balance === 0
      ? "0"
      : `${prefix || ""} ${new Intl.NumberFormat("cs-CZ", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(balance ?? 0)}`;

  return (
    <Box display="flex" gap={2} alignItems="baseline">
      <Typography variant="h2" color={textColor}>
        {isLoading ? <Skeleton width={140} height={48} /> : formattedValue}
      </Typography>
      {!isLoading && (
        <Currency variant={isBigCurrency ? "h2" : "h6"} color={textColor}>
          {currencySymbol}
        </Currency>
      )}
    </Box>
  );
};
